import React, { useState, useEffect, useRef  } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Swal from "sweetalert2";
import ApiService from "../util/ApiService";
import Spinners from "../../../Components/Common/Spinner";
import UserModal from "./UserModal";
import { FaSun, FaCloudSun, FaMoon } from "react-icons/fa";
import "./assets/css/TasksKanban.css";
import dayjs from "dayjs";

const dayOfWeekMap = {
    0: "Domingo",
    1: "Segunda-feira",
    2: "Terça-feira",
    3: "Quarta-feira",
    4: "Quinta-feira",
    5: "Sexta-feira",
    6: "Sábado",
};

const getPeriodStyle = (time) => {
    const hour = parseInt(time.split(":")[0], 10);

    if (hour >= 5 && hour < 12) {
        return {
            color: "#FFF8DC", // light pastel yellow (Manhã)
            icon: <FaSun size={14} style={{ opacity: 0.7 }} />
        };
    }

    if (hour >= 12 && hour < 19) {
        return {
            color: "#FFE4B5", // light pastel orange (Tarde)
            icon: <FaCloudSun size={14} style={{ opacity: 0.7 }} />
        };
    }

    return {
        color: "#E6F0FA", // light pastel blue (Noite)
        icon: <FaMoon size={14} style={{ opacity: 0.7 }} />
    };
};

const TasksKanban = ({ arraScale = {}, isLoading, handleEdit, fetchArray, filtersPeriod }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [columns, setColumns] = useState([]);



    const kanbanRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [kanbanWidth, setKanbanWidth] = useState(0);


    useEffect(() => {
        const syncScroll = () => {
            if (kanbanRef.current && scrollbarRef.current) {
                scrollbarRef.current.scrollLeft = kanbanRef.current.scrollLeft;
            }
        };

        const updateWidth = () => {
            if (kanbanRef.current) {
                setKanbanWidth(kanbanRef.current.scrollWidth);
            }
        };

        updateWidth();
        const el = kanbanRef.current;
        el?.addEventListener("scroll", syncScroll);
        window.addEventListener("resize", updateWidth);

        return () => {
            el?.removeEventListener("scroll", syncScroll);
            window.removeEventListener("resize", updateWidth);
        };
    }, [arraScale]);


    useEffect(() => {
        let isSyncingScroll = false;

        const syncScrollFromKanban = () => {
            if (isSyncingScroll) return;
            isSyncingScroll = true;
            scrollbarRef.current.scrollLeft = kanbanRef.current.scrollLeft;
            setTimeout(() => (isSyncingScroll = false), 0);
        };

        const syncScrollFromScrollbar = () => {
            if (isSyncingScroll) return;
            isSyncingScroll = true;
            kanbanRef.current.scrollLeft = scrollbarRef.current.scrollLeft;
            setTimeout(() => (isSyncingScroll = false), 0);
        };

        const updateWidth = () => {
            if (kanbanRef.current) {
                setKanbanWidth(kanbanRef.current.scrollWidth + 100);
            }
        };

        updateWidth();

        const kanbanEl = kanbanRef.current;
        const scrollEl = scrollbarRef.current;

        kanbanEl?.addEventListener("scroll", syncScrollFromKanban);
        scrollEl?.addEventListener("scroll", syncScrollFromScrollbar);
        window.addEventListener("resize", updateWidth);

        return () => {
            kanbanEl?.removeEventListener("scroll", syncScrollFromKanban);
            scrollEl?.removeEventListener("scroll", syncScrollFromScrollbar);
            window.removeEventListener("resize", updateWidth);
        };
    }, [arraScale]);



    useEffect(() => {
        const handleScroll = () => {
            const headers = document.querySelectorAll('.column-header');
            const scrollTop = window.scrollY;

            headers.forEach(header => {
                const column = header.closest('.tasks-list');
                const columnRect = column.getBoundingClientRect();
                const offsetTop = 68; // altura da barra de filtro

                if (columnRect.top <= offsetTop) {
                    header.style.position = 'fixed';
                    header.style.top = `${offsetTop}px`;
                    header.style.left = `${columnRect.left}px`;
                    header.style.width = `${column.offsetWidth}px`;
                    header.style.zIndex = '50';
                    header.style.background = '#fff';
                    header.classList.add('is-fixed');
                } else {
                    header.style.position = 'static';
                    header.style.top = 'unset';
                    header.style.left = 'unset';
                    header.style.width = 'unset';
                    header.style.zIndex = 'unset';
                    header.classList.remove('is-fixed');
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);


    useEffect(() => {
        const allDays = Object.keys(dayOfWeekMap).reduce((acc, day) => {
            acc[day] = arraScale[day] || [];
            return acc;
        }, {});

        const formattedColumns = Object.entries(allDays).map(([day, scales]) => {
            const filteredScales = scales.filter(scale => {
                const hour = parseInt(scale.time_begin.split(":")[0], 10);

                // Filtros por período
                const isMorning = hour >= 5 && hour < 12;
                const isLate = hour >= 12 && hour < 19;
                const isNight = hour < 5 || hour >= 19;

                const periodMatch =
                    (isMorning && filtersPeriod.morning) ||
                    (isLate && filtersPeriod.late) ||
                    (isNight && filtersPeriod.night);

                // Novo filtro: se ativado, mostrar apenas escalas sem usuário
                const userNotFilledMatch = !filtersPeriod.user_not_filled || !scale.user?.id;

                const userConflictsMatch = !filtersPeriod.user_conflicts || (scale.conflict != null);


                return periodMatch && userNotFilledMatch && userConflictsMatch;
            });

            return {
                id: day,
                name: dayOfWeekMap[parseInt(day)],
                cards: filteredScales.map(scale => ({
                    id: String(scale.id),
                    title: scale.hospital.name,
                    description: `${scale.hospital.city} - ${scale.hospital.uf}`,
                    user: scale.user,
                    department: scale.department.name,
                    scale: scale
                }))
            };
        });

        setColumns(formattedColumns);
    }, [arraScale, filtersPeriod]);

    const updateCardDay = (scale, newDay, cardId) => {
        const payload = {
            ...scale,
            time_begin: scale.time_begin.slice(0, 5),
            time_end: scale.time_end.slice(0, 5),
            duty_scale_day_wee_id: newDay
        };

        ApiService.send("put", `${process.env.REACT_APP_URL_API}scale/update`, payload, true)
            .then(response => {
                Swal.fire({
                    icon: "success",
                    title: "Dia da semana atualizado!",
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });

                setColumns((prevColumns) => {
                    let movedCard = null;

                    const updatedColumns = prevColumns.map(column => {
                        const newCards = column.cards.filter(card => {
                            if (card.id === cardId) {
                                movedCard = { ...card }; // salva o card removido
                                return false;
                            }
                            return true;
                        });
                        return { ...column, cards: newCards };
                    });

                    if (movedCard) {
                        movedCard.scale.duty_scale_day_wee_id = parseInt(newDay);
                        const targetColumnIndex = updatedColumns.findIndex(col => col.id === newDay);
                        if (targetColumnIndex !== -1) {
                            updatedColumns[targetColumnIndex].cards.push(movedCard);
                        }
                    }

                    return updatedColumns;
                });
            })
            .catch(error => {
                ApiService.handleApiError(error);
            });
    };

    const toastCounterRef = useRef(0);
    const showToast = (message) => {
        const delay = toastCounterRef.current * 2500;
        toastCounterRef.current += 1;

        setTimeout(() => {
            Swal.fire({
                icon: "success",
                title: message,
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });

            // Decrementa o contador depois que o toast sumir
            setTimeout(() => {
                toastCounterRef.current -= 1;
            }, 2500);
        }, delay);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const newDay = destination.droppableId;

        // Movimento dentro do mesmo dia (mesma coluna)
        if (source.droppableId === destination.droppableId && source.index !== destination.index) {
            setColumns((prevColumns) => {
                const newColumns = [...prevColumns];
                const column = newColumns.find(col => col.id === source.droppableId);
                if (!column) return prevColumns;

                const cards = [...column.cards];
                const [movedCard] = cards.splice(source.index, 1);
                cards.splice(destination.index, 0, movedCard); // move para nova posição

                // Captura os horários em ordem original
                const affectedIndexes = source.index > destination.index
                    ? cards.slice(destination.index, source.index + 1)
                    : cards.slice(source.index, destination.index + 1).reverse();

                const originalTimes = affectedIndexes.map(card => ({
                    time_begin: card.scale.time_begin,
                    time_end: card.scale.time_end
                }));

                // Aplica rotação de horários
                for (let i = 0; i < affectedIndexes.length; i++) {
                    const nextIndex = (i + 1) % originalTimes.length;
                    affectedIndexes[i].scale.time_begin = originalTimes[nextIndex].time_begin;
                    affectedIndexes[i].scale.time_end = originalTimes[nextIndex].time_end;
                }

                // Atualiza API com os horários rotacionados
                const promises = affectedIndexes.map((card) => {
                    const payload = {
                        ...card.scale,
                        time_begin: card.scale.time_begin.slice(0, 5),
                        time_end: card.scale.time_end.slice(0, 5),
                    };

                    return ApiService.send("put", `${process.env.REACT_APP_URL_API}scale/update`, payload, true)
                        .then(() => `Plantão atualizado: ${payload.time_begin} - ${payload.time_end}`);
                });

                Promise.all(promises)
                    .then((messages) => {
                        Swal.fire({
                            icon: "success",
                            title: "Horários rotacionados com sucesso",
                            html: messages.join("<br>"),
                            toast: true,
                            position: "top-end",
                            showConfirmButton: false,
                            timer: 4000,
                            timerProgressBar: true,
                        });
                    })
                    .catch((err) => {
                        console.error("Erro ao atualizar horários:", err);
                    });

                column.cards = cards;
                return newColumns;
            });
        }


        // Movimento entre dias (colunas diferentes)
        if (source.droppableId !== destination.droppableId) {
            setColumns((prevColumns) => {
                const newColumns = prevColumns.map(col => ({ ...col, cards: [...col.cards] }));

                const sourceColumn = newColumns.find(col => col.id === source.droppableId);
                const destColumn = newColumns.find(col => col.id === destination.droppableId);

                if (!sourceColumn || !destColumn) return prevColumns;

                const [movedCard] = sourceColumn.cards.splice(source.index, 1);
                destColumn.cards.splice(destination.index, 0, movedCard);

                // Atualiza o dia da escala
                updateCardDay(movedCard.scale, destination.droppableId);

                return newColumns;
            });
        }
    };


    const handleDelete = (id) => {
        Swal.fire({
            title: "Tem certeza?",
            text: "Esta ação não pode ser desfeita!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then((result) => {
            if (result.isConfirmed) {
                ApiService.send("delete", `${process.env.REACT_APP_URL_API}scale/delete`, {id: id}, true)
                    .then(() => {
                        Swal.fire({
                            title: "Excluído!",
                            text: "A escala foi removida com sucesso.",
                            icon: "success",
                            timer: 2000,
                            showConfirmButton: false
                        });

                        // Atualizar a lista de escalas removendo do estado
                        setColumns((prev) => prev.map((col) => ({
                            ...col,
                            cards: col.cards.filter(card => card.id !== id)
                        })));

                    })
                    .catch((error) => {
                        console.error("Erro ao excluir:", error);
                        Swal.fire({
                            title: "Erro!",
                            text: "Não foi possível excluir a escala.",
                            icon: "error",
                            confirmButtonText: "OK"
                        });
                    });
            }
        });
    };



    const onclose = (scale = false) => {
        setShowModal(false);
        if (scale) {
            fetchArray(true);
        }
    };



    return (
        <div style={{ position: 'relative' }}>
        <div ref={kanbanRef}
        >
            <div className="tasks-board mb-3" id="kanbanboard">
                {isLoading ? (
                    <Spinners/>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        {columns.map(column => (
                            <div className="tasks-list" key={column.id}
                                 style={{
                                     minWidth: "180px", // garante largura mínima para leitura
                                     margin: "0px",
                                   //  maxWidth: "100%",
                                     //  flex: "1 1 auto",
                                 }}
                            >
                                <div className="column-header">
                                    <h6 className="text-uppercase fw-semibold text-center">{column.name}</h6>
                                    <div className="text-center">
                                        <span className="badge bg-info">{column.cards.length} Escalas</span>
                                    </div>
                                </div>

                                <div className="tasks-wrapper">
                                    <Droppable droppableId={column.id}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                {column.cards.length === 0 ? (
                                                    <p className="card-sem-escalas text-center text-muted">&nbsp;</p>
                                                ) : (
                                                    column.cards.map((card, index) => {
                                                        const style = getPeriodStyle(card.scale.time_begin);
                                                        return (
                                                            <Draggable key={card.id} draggableId={card.id}
                                                                       index={index}>
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`pb-0 task-list ${snapshot.isDragging ? 'dragging' : ''}`}
                                                                    >
                                                                        <div
                                                                            className={`task-box mt-2 
                                                                                        ${!card.scale.user?.id ? 'no-user' : ''} 
                                                                                        ${card.scale.conflict ? 'has-conflict' : ''}`}




                                                                            style={{
                                                                                backgroundColor: style.color,
                                                                                borderRadius: '10px',
                                                                                padding: '12px'
                                                                            }}
                                                                        >




                                                                            {card.scale.conflict && (
                                                                                <div className="conflict-tooltip" style={{ position: 'absolute', top: '-30px', right: '10px' }}>
                                                                                    <i className="fa-solid fa-ban" style={{ fontSize: '18px', color: '#b10b0b', cursor: 'pointer' }}></i>

                                                                                    <div className="tooltip-content">
                                                                                        <div className="fw-bold text-danger mb-1">Conflito detectado</div>
                                                                                        <div><strong>Plantão:</strong> {dayjs(card.scale.conflict.new_duty.date_time).format('DD/MM HH:mm')} - {dayjs(card.scale.conflict.new_duty.date_time_end).format('HH:mm')}</div>
                                                                                        <div><strong>Dia:</strong> {card.scale.conflict.new_duty.day_of_week}</div>
                                                                                        <div><strong>Usuário:</strong> {card.scale.conflict.new_duty.name}</div>
                                                                                        <hr className="my-1" />
                                                                                        <div><strong>Conflita com:</strong></div>
                                                                                       <div><strong>Data:</strong> {dayjs(card.scale.conflict.conflicting_duty.date_time).format('DD/MM HH:mm')} - {dayjs(card.scale.conflict.conflicting_duty.date_time_end).format('HH:mm')}</div>
                                                                                        <div><strong>Responsável:</strong> {card.scale.conflict.conflicting_duty.managers}</div>
                                                                                    </div>
                                                                                </div>
                                                                            )}


                                                                            <div
                                                                                        className="task-card h-100 d-flex flex-column justify-content-between">
                                                                                        <div>
                                                                                                <span
                                                                                                    className="task-title text-wrap d-block mb-2">
                                                                                                  {style.icon} {card.title}
                                                                                                </span>
                                                                                            <span
                                                                                                className="user-hover mt-2"
                                                                                                style={{
                                                                                                    cursor: "pointer",
                                                                                                    display: "inline-flex",
                                                                                                    alignItems: "center",
                                                                                                    gap: "5px"
                                                                                                }}
                                                                                                onClick={() => {
                                                                                                    setSelectedCard(card);
                                                                                                    setShowModal(true);
                                                                                                }}
                                                                                            >
                                                                                              <img
                                                                                                  className="user-avatar"
                                                                                                  src={card.scale.user?.profile_image || process.env.REACT_APP_URL_STORAGE + "src/perfil_icone.png"}
                                                                                                  width={20}
                                                                                                  alt="Avatar"
                                                                                              />
                                                                                              <span className="user-name">{card.scale.user.name}</span>
                                                                                            </span>
                                                                                            <p className="text-muted mt-2">
                                                                                                {card.scale.order_month === 0 ? (
                                                                                                    <><i
                                                                                                        className="fa fa-calendar text-info me-1"></i> Todas
                                                                                                        as semanas</>
                                                                                                ) : (
                                                                                                    <><i
                                                                                                        className="fa fa-calendar-week text-info me-1"></i> {card.scale.order_month}ª
                                                                                                        semana</>
                                                                                                )}
                                                                                            </p>
                                                                                            <p className="text-muted mt-1">
                                                                                                <i className="fa fa-building text-info me-1"></i> {card.department}
                                                                                            </p>
                                                                                            <p className="text-muted">
                                                                                                <i className="fa fa-calendar text-info"></i> {card.scale.time_begin.slice(0, 5)} - {card.scale.time_end.slice(0, 5)} ({card?.scale?.total_hours?.slice(0, 5)})
                                                                                            </p>
                                                                                        </div>
                                                                                        <div
                                                                                            className="d-flex justify-content-center gap-2 mt-3">
                                                                                            <button type="button"
                                                                                                className="btn btn-danger btn-kanban btn-small"
                                                                                                onClick={() => handleDelete(card.id)}>
                                                                                                <i className="fa fa-close me-1"></i> Excluir
                                                                                            </button>
                                                                                            <button type="button"
                                                                                                className="btn btn-success btn-kanban btn-small"
                                                                                                onClick={() => handleEdit(card.scale)}>
                                                                                                <i className="fa fa-edit me-1"></i> Editar
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>


                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                        ))}
                    </DragDropContext>
                )}
                {showModal && <UserModal show={showModal} scale={selectedCard.scale} onHide={onclose}/>}
            </div>
        </div>

        </div>

    );
};

export default TasksKanban;
